import getOrCreateVoiceTabSessionId from './voiceTabSession';
import { emitSdkEvent, getVoiceAgentState } from './voiceAgentSessionStore';
import { isAgentCallDead, isTerminalSessionState } from './voiceJsSipSessionStatus';
import { logWebRtcEvent, logWebRtcFailure } from './voiceWebRtcLog';

/** @type {WeakMap<object, { token: number, timers: ReturnType<typeof setTimeout>[] }>} */
const watchdogsBySession = new WeakMap();

export const AGENT_WEBRTC_MEDIA_FAILED = 'agent_webrtc_media_failed';

/** Samples: early progress + fail window for slow TURN. */
export const VOICE_ICE_MEDIA_WATCH_SAMPLE_MS = Object.freeze([0, 3000, 6000, 10000, 15000, 20000]);

/** Firefox: longer window only — do NOT restartIce without SIP renegotiation (JsSIP maps ice failed → RTP Timeout). */
export const VOICE_ICE_MEDIA_WATCH_SAMPLE_MS_FIREFOX = Object.freeze([
  0, 3000, 6000, 10000, 15000, 20000
]);

/** Hang if still ICE checking / failed with 0 RTP after this. */
export const VOICE_ICE_MEDIA_FAIL_DEADLINE_MS = 15_000;

export const VOICE_ICE_MEDIA_FAIL_DEADLINE_MS_FIREFOX = 20_000;

/**
 * If ICE already connected/completed but RTP is still 0, wait until this before zero_rtp fail.
 * Avoids hanging a healthy ICE path that just needs a moment for first packets.
 */
export const VOICE_ICE_MEDIA_RTP_GRACE_DEADLINE_MS = 20_000;

export const VOICE_ICE_MEDIA_RTP_GRACE_DEADLINE_MS_FIREFOX = 25_000;

function isFirefoxBrowser() {
  return typeof navigator !== 'undefined'
    && /firefox/i.test(navigator.userAgent || '');
}

/**
 * @param {RTCPeerConnection|null|undefined} pc
 * @returns {Record<string, unknown>}
 */
function describeMediaTracks(pc) {
  const receivers = pc?.getReceivers?.() ?? [];
  const senders = pc?.getSenders?.() ?? [];
  return {
    receivers: receivers
      .filter((entry) => entry.track?.kind === 'audio')
      .map((entry) => ({
        muted: entry.track.muted,
        enabled: entry.track.enabled,
        readyState: entry.track.readyState
      })),
    senders: senders
      .filter((entry) => entry.track?.kind === 'audio')
      .map((entry) => ({
        muted: entry.track.muted,
        enabled: entry.track.enabled,
        readyState: entry.track.readyState
      }))
  };
}

/**
 * @param {RTCIceServer[]|undefined} iceServers
 * @returns {Array<{ urls: string|string[], hasCredential: boolean }>}
 */
export function summarizeIceServersForLog(iceServers) {
  if (!Array.isArray(iceServers)) return [];
  return iceServers.map((entry) => {
    const urls = Array.isArray(entry.urls) ? entry.urls : [entry.urls];
    const list = urls.filter((url) => typeof url === 'string');
    return {
      urlCount: list.length,
      hasTcp: list.some((url) => /[?&;]transport=tcp\b/i.test(url) || /^turns:/i.test(url)),
      // Hosts only — never log full turn URLs with embedded secrets in query.
      hosts: list.map((url) => {
        try {
          return url.replace(/^turns?:/i, '').split('?')[0].split(';')[0];
        } catch {
          return 'invalid';
        }
      }),
      hasCredential: Boolean(entry.credential)
    };
  });
}

/**
 * Real media for UI / health — never use element.play() / remoteAudioPlaying.
 *
 * @param {{
 *   iceConnectionState?: string,
 *   bytesSent?: number,
 *   bytesReceived?: number,
 *   pairBytesSent?: number,
 *   pairBytesReceived?: number
 * }|null|undefined} stats
 * @param {import('jssip').RTCSession|null|undefined} [session]
 * @returns {boolean}
 */
export function isAgentRealtimeMediaOk(stats, session) {
  const ice = stats?.iceConnectionState
    || session?.connection?.iceConnectionState
    || '';
  if (ice === 'connected' || ice === 'completed') {
    return true;
  }
  const bytesReceived = stats?.bytesReceived ?? 0;
  const bytesSent = stats?.bytesSent ?? 0;
  const pairIn = stats?.pairBytesReceived ?? 0;
  const pairOut = stats?.pairBytesSent ?? 0;
  return bytesReceived > 0 || bytesSent > 0 || pairIn > 0 || pairOut > 0;
}

/**
 * @param {RTCStatsReport} stats
 * @param {string|undefined} candidateId
 * @returns {{ type?: string, protocol?: string, address?: string }|null}
 */
function lookupCandidate(stats, candidateId) {
  if (!candidateId) return null;
  const report = stats.get(candidateId);
  if (!report) return null;
  return {
    type: report.candidateType || report.type,
    protocol: report.protocol,
    address: report.address || report.ip
  };
}

/** Compact candidate summary for logs (no ids/foundation noise). */
function summarizeCandidateReport(report) {
  const url = typeof report.url === 'string' ? report.url : '';
  const summary = {
    side: report.type === 'local-candidate' ? 'local' : 'remote',
    type: report.candidateType || report.type || null,
    protocol: report.protocol ?? null,
    address: `${report.address || report.ip || '?'}:${report.port ?? '?'}`
  };
  if (url) {
    try {
      [summary.relayUrlHost] = url.replace(/^turns?:\/\//i, '').split(/[:?]/);
    } catch {
      // ignore malformed url
    }
  }
  return summary;
}

const AGENT_ICE_PAIR_CENSUS_LIMIT = 12;
const AGENT_ICE_CANDIDATE_CENSUS_LIMIT = 16;

/**
 * Full ICE pair/candidate census for Firefox diagnosis: when checks die silently we need
 * which pairs exist, their states, and whether STUN requests go out without responses.
 *
 * @param {RTCStatsReport} stats
 */
function summarizeIceCensus(stats) {
  /** @type {Map<string, object>} */
  const candidatesById = new Map();
  /** @type {Array<object>} */
  const pairs = [];
  let pairCount = 0;
  let requestsSent = 0;
  let requestsReceived = 0;
  let responsesSent = 0;
  let responsesReceived = 0;
  let consentRequestsSent = 0;

  stats.forEach((report) => {
    if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
      candidatesById.set(report.id, summarizeCandidateReport(report));
      return;
    }
    if (report.type !== 'candidate-pair') return;
    pairCount += 1;
    requestsSent += report.requestsSent || 0;
    requestsReceived += report.requestsReceived || 0;
    responsesSent += report.responsesSent || 0;
    responsesReceived += report.responsesReceived || 0;
    consentRequestsSent += report.consentRequestsSent || 0;
    pairs.push({
      state: report.state ?? null,
      nominated: Boolean(report.nominated),
      selected: Boolean(report.selected),
      priority: report.priority ?? null,
      bytesSent: report.bytesSent || 0,
      bytesReceived: report.bytesReceived || 0,
      requestsSent: report.requestsSent || 0,
      requestsReceived: report.requestsReceived || 0,
      responsesSent: report.responsesSent || 0,
      responsesReceived: report.responsesReceived || 0,
      currentRoundTripTime: report.currentRoundTripTime ?? null,
      lastPacketReceivedTimestamp: report.lastPacketReceivedTimestamp ?? null,
      localCandidateId: report.localCandidateId ?? null,
      remoteCandidateId: report.remoteCandidateId ?? null
    });
  });

  const summarizePairSide = (summary) => (summary
    ? { type: summary.type, protocol: summary.protocol, address: summary.address }
    : null);

  const limitedPairs = pairs.slice(0, AGENT_ICE_PAIR_CENSUS_LIMIT);
  const resolvedPairs = limitedPairs.map((pair) => ({
    ...pair,
    localCandidateId: undefined,
    remoteCandidateId: undefined,
    local: summarizePairSide(candidatesById.get(pair.localCandidateId)),
    remote: summarizePairSide(candidatesById.get(pair.remoteCandidateId))
  }));
  const formatPairSideCompact = (side) => (side
    ? `${side.protocol || '?'}/${side.type || '?'}@${side.address || '?'}`
    : '?');
  const limitedCandidates = [...candidatesById.values()]
    .slice(0, AGENT_ICE_CANDIDATE_CENSUS_LIMIT);

  return {
    pairCount,
    checks: {
      requestsSent, requestsReceived, responsesSent, responsesReceived, consentRequestsSent
    },
    checksCompact: `sent=${requestsSent} gotReq=${requestsReceived} resp=${responsesReceived} sentResp=${responsesSent} consent=${consentRequestsSent}`,
    candidatePairs: resolvedPairs,
    pairsCompact: resolvedPairs.map((pair) => (
      `${formatPairSideCompact(pair.local)}->${formatPairSideCompact(pair.remote)}`
      + ` ${pair.state || '?'} sent=${pair.requestsSent} resp=${pair.responsesReceived}`
      + ` bytes=${pair.bytesSent}/${pair.bytesReceived}`
    )).join(' | ') || 'none',
    candidates: limitedCandidates,
    candidatesCompact: limitedCandidates
      .map((candidate) => `${candidate.side}:${formatPairSideCompact(candidate)}`)
      .join(' ') || 'none'
  };
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {Promise<object|null>}
 */
export async function collectAgentRtcAudioStats(session) {
  const pc = session?.connection;
  if (!pc?.getStats) return null;

  const senders = pc.getSenders?.() ?? [];
  const audioMuted = Boolean(session?.isMuted?.().audio);
  senders.forEach((sender) => {
    if (sender.track?.kind === 'audio') {
      const audioTrack = sender.track;
      // Re-enable is only safe while the session is unmuted — never override an explicit mute.
      audioTrack.enabled = !audioMuted;
    }
  });

  let stats;
  try {
    stats = await pc.getStats();
  } catch {
    return null;
  }

  let bytesSent = 0;
  let packetsSent = 0;
  let bytesReceived = 0;
  let packetsReceived = 0;
  let inboundReports = 0;
  let outboundReports = 0;
  let pairBytesReceived = 0;
  let pairBytesSent = 0;
  /** @type {object|null} */
  let selectedCandidatePair = null;

  stats.forEach((report) => {
    const isAudio = report.kind === 'audio' || report.mediaType === 'audio' || !report.kind;
    if (report.type === 'outbound-rtp' && isAudio) {
      outboundReports += 1;
      bytesSent += report.bytesSent || 0;
      packetsSent += report.packetsSent || 0;
    }
    if (report.type === 'inbound-rtp' && isAudio) {
      inboundReports += 1;
      bytesReceived += report.bytesReceived || 0;
      packetsReceived += report.packetsReceived || 0;
    }
    if (
      report.type === 'candidate-pair'
      && (report.nominated || report.selected || report.state === 'succeeded')
    ) {
      pairBytesReceived = Math.max(pairBytesReceived, report.bytesReceived || 0);
      pairBytesSent = Math.max(pairBytesSent, report.bytesSent || 0);
      if (!selectedCandidatePair || report.selected || report.nominated) {
        const local = lookupCandidate(stats, report.localCandidateId);
        const remote = lookupCandidate(stats, report.remoteCandidateId);
        selectedCandidatePair = {
          state: report.state,
          nominated: report.nominated,
          selected: report.selected,
          bytesReceived: report.bytesReceived || 0,
          bytesSent: report.bytesSent || 0,
          currentRoundTripTime: report.currentRoundTripTime,
          localCandidateType: local?.type ?? null,
          remoteCandidateType: remote?.type ?? null,
          localProtocol: local?.protocol ?? null,
          remoteProtocol: remote?.protocol ?? null
        };
      }
    }
  });

  return {
    bytesSent,
    packetsSent,
    bytesReceived,
    packetsReceived,
    inboundReports,
    outboundReports,
    pairBytesReceived,
    pairBytesSent,
    iceConnectionState: pc.iceConnectionState,
    iceGatheringState: pc.iceGatheringState,
    connectionState: pc.connectionState,
    selectedCandidatePair,
    ...summarizeIceCensus(stats),
    mediaOk: isAgentRealtimeMediaOk({
      iceConnectionState: pc.iceConnectionState,
      bytesSent,
      bytesReceived,
      pairBytesSent,
      pairBytesReceived
    }),
    ...describeMediaTracks(pc)
  };
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 * @param {string} reason
 * @param {Record<string, unknown>} [extra]
 */
export async function logAgentRtcAudioStats(session, reason, extra = {}) {
  const stats = await collectAgentRtcAudioStats(session);
  if (!stats) return null;
  const payload = {
    reason,
    tabSessionId: getOrCreateVoiceTabSessionId(),
    ...stats,
    ...extra
  };
  logWebRtcEvent('sdk.rtcAudioStats', payload);
  return stats;
}

/**
 * ICE still negotiating / failed (not "connected but waiting for first RTP").
 *
 * @param {{ iceConnectionState?: string, connectionState?: string }|null} stats
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isAgentIceStuck(stats, session) {
  const ice = stats?.iceConnectionState
    || session?.connection?.iceConnectionState
    || '';
  const conn = stats?.connectionState
    || session?.connection?.connectionState
    || '';
  if (ice === 'failed' || conn === 'failed' || ice === 'closed' || conn === 'closed') {
    return true;
  }
  return ice === 'checking' || ice === 'new' || ice === '' || conn === 'connecting';
}

/**
 * Local ICE still allocating (common right after answer with relay-only TURN).
 * @param {{ iceGatheringState?: string }|null|undefined} stats
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function isAgentIceStillGathering(stats, session) {
  const gathering = stats?.iceGatheringState
    || session?.connection?.iceGatheringState
    || '';
  return gathering === 'gathering' || gathering === 'new';
}

/**
 * Relay-only answer without any typ relay in local SDP → TURN allocate failed
 * (Firefox often surfaces this as iceCandidateError 701).
 *
 * @param {import('jssip').RTCSession|null|undefined} session
 * @returns {boolean}
 */
export function sessionLocalSdpHasRelayCandidate(session) {
  try {
    const sdp = session?.connection?.localDescription?.sdp || '';
    return typeof sdp === 'string' && sdp.includes('typ relay');
  } catch {
    return false;
  }
}

/**
 * @deprecated Prefer isAgentIceStuck + isAgentRealtimeMediaOk for fail decisions.
 * Kept for tests that check "unhealthy = stuck OR zero RTP".
 */
export function isAgentIceMediaUnhealthy(session, stats) {
  if (!session || isAgentCallDead(session) || isTerminalSessionState(session.status)) {
    return false;
  }
  if (isAgentIceStuck(stats, session)) return true;
  return !isAgentRealtimeMediaOk(stats, session);
}

function clearWatchdogTimers(entry) {
  if (!entry?.timers) return;
  entry.timers.forEach((timerId) => clearTimeout(timerId));
  entry.timers.splice(0, entry.timers.length);
}

/**
 * @param {import('jssip').RTCSession|null|undefined} session
 */
export function stopAgentIceMediaWatchdog(session) {
  if (!session) return;
  const entry = watchdogsBySession.get(session);
  if (!entry) return;
  entry.token += 1;
  clearWatchdogTimers(entry);
  watchdogsBySession.delete(session);
}

/**
 * After SIP answer: sample ICE/RTP. Distinguish:
 * - sip answered / confirmed (session layer)
 * - ICE connected
 * - RTP flowing (bytes)
 *
 * Hang on ice_stuck after failDeadline if still checking + 0 bytes.
 * If ICE already connected, allow RTP grace before zero_rtp hang.
 *
 * @param {import('jssip').RTCSession} session
 * @param {{
 *   callControlId?: string|null,
 *   failDeadlineMs?: number,
 *   rtpGraceDeadlineMs?: number,
 *   sampleMs?: number[],
 *   pcConfig?: RTCConfiguration
 * }} [options]
 */
export function startAgentIceMediaWatchdog(session, options = {}) {
  if (!session) return;
  stopAgentIceMediaWatchdog(session);

  const firefox = isFirefoxBrowser();
  const failDeadlineMs = options.failDeadlineMs
    ?? (firefox ? VOICE_ICE_MEDIA_FAIL_DEADLINE_MS_FIREFOX : VOICE_ICE_MEDIA_FAIL_DEADLINE_MS);
  const rtpGraceDeadlineMs = options.rtpGraceDeadlineMs
    ?? (firefox ? VOICE_ICE_MEDIA_RTP_GRACE_DEADLINE_MS_FIREFOX : VOICE_ICE_MEDIA_RTP_GRACE_DEADLINE_MS);
  const sampleMs = options.sampleMs
    ?? (firefox ? VOICE_ICE_MEDIA_WATCH_SAMPLE_MS_FIREFOX : VOICE_ICE_MEDIA_WATCH_SAMPLE_MS);
  const callControlId = typeof options.callControlId === 'string'
    ? options.callControlId.trim()
    : (getVoiceAgentState().agentAnswerBridgeCallControlId
      || getVoiceAgentState().pendingAutoAnswerCallControlId
      || null);
  const tabSessionId = getOrCreateVoiceTabSessionId();
  const pcConfig = options.pcConfig || getVoiceAgentState().agentPeerConnectionConfig;

  const entry = { token: 0, timers: [] };
  watchdogsBySession.set(session, entry);
  const { token } = entry;

  logWebRtcEvent('sdk.iceMediaWatchStarted', {
    id: session.id,
    callControlId,
    tabSessionId,
    failDeadlineMs,
    rtpGraceDeadlineMs,
    firefox,
    iceTransportPolicy: pcConfig?.iceTransportPolicy,
    iceServers: summarizeIceServersForLog(pcConfig?.iceServers)
  });

  const failMedia = (stats, reason) => {
    if (watchdogsBySession.get(session)?.token !== token) return;
    stopAgentIceMediaWatchdog(session);

    const error = new Error(
      reason === 'turn_unreachable'
        ? 'TURN allocate failed (errorCode: 701 / no relay candidates)'
        : 'WebRTC media failed: ICE/TURN relay did not carry RTP (Sin media WebRTC/TURN)'
    );
    error.code = reason === 'turn_unreachable'
      ? 'TURN_ALLOCATE'
      : AGENT_WEBRTC_MEDIA_FAILED;

    const details = {
      reason,
      id: session.id,
      callControlId,
      tabSessionId,
      failDeadlineMs,
      rtpGraceDeadlineMs,
      iceTransportPolicy: pcConfig?.iceTransportPolicy,
      iceServers: summarizeIceServersForLog(pcConfig?.iceServers),
      iceConnectionState: stats?.iceConnectionState || session.connection?.iceConnectionState,
      connectionState: stats?.connectionState || session.connection?.connectionState,
      bytesSent: stats?.bytesSent ?? 0,
      bytesReceived: stats?.bytesReceived ?? 0,
      pairBytesSent: stats?.pairBytesSent ?? 0,
      pairBytesReceived: stats?.pairBytesReceived ?? 0,
      inboundReports: stats?.inboundReports ?? 0,
      selectedCandidatePair: stats?.selectedCandidatePair ?? null,
      checks: stats?.checks ?? null,
      checksCompact: stats?.checksCompact ?? null,
      candidatePairs: stats?.candidatePairs ?? [],
      pairsCompact: stats?.pairsCompact ?? null,
      candidates: stats?.candidates ?? [],
      candidatesCompact: stats?.candidatesCompact ?? null,
      sipAnswered: true,
      iceConnected: !isAgentIceStuck(stats, session),
      rtpFlowing: isAgentRealtimeMediaOk(stats, session)
        && ((stats?.bytesReceived ?? 0) > 0 || (stats?.bytesSent ?? 0) > 0)
    };

    logWebRtcFailure('sdk.iceMediaFailed', details);
    emitSdkEvent({
      type: 'mediaFailed',
      session,
      error,
      callControlId,
      tabSessionId,
      stats: details
    });
  };

  sampleMs.forEach((delayMs) => {
    const timerId = setTimeout(async () => {
      if (watchdogsBySession.get(session)?.token !== token) return;
      if (isAgentCallDead(session) || isTerminalSessionState(session.status)) {
        stopAgentIceMediaWatchdog(session);
        return;
      }

      const stats = await collectAgentRtcAudioStats(session);
      const sampleReason = `post-answer+${delayMs / 1000}s`;
      const iceStuck = isAgentIceStuck(stats, session);
      const stillGathering = isAgentIceStillGathering(stats, session);
      const mediaOk = isAgentRealtimeMediaOk(stats, session);
      const hasRtpBytes = ((stats?.bytesSent ?? 0) > 0)
        || ((stats?.bytesReceived ?? 0) > 0)
        || ((stats?.pairBytesSent ?? 0) > 0)
        || ((stats?.pairBytesReceived ?? 0) > 0);

      logWebRtcEvent('sdk.rtcAudioStats', {
        reason: sampleReason,
        callControlId,
        tabSessionId,
        iceTransportPolicy: pcConfig?.iceTransportPolicy,
        sipAnswered: true,
        iceConnected: !iceStuck,
        iceGatheringState: stats?.iceGatheringState
          || session.connection?.iceGatheringState,
        rtpFlowing: hasRtpBytes,
        mediaOk,
        ...(stats || {})
      });

      if (delayMs >= failDeadlineMs) {
        logWebRtcFailure('sdk.iceMediaWatchSample', {
          reason: sampleReason,
          callControlId,
          tabSessionId,
          iceConnectionState: stats?.iceConnectionState || session.connection?.iceConnectionState,
          iceGatheringState: stats?.iceGatheringState || session.connection?.iceGatheringState,
          connectionState: stats?.connectionState || session.connection?.connectionState,
          bytesSent: stats?.bytesSent ?? 0,
          bytesReceived: stats?.bytesReceived ?? 0,
          sipAnswered: true,
          iceConnected: !iceStuck,
          rtpFlowing: hasRtpBytes,
          selectedCandidatePair: stats?.selectedCandidatePair ?? null
        });
      }

      if (mediaOk && hasRtpBytes) {
        logWebRtcEvent('sdk.iceMediaWatchHealthy', {
          id: session.id,
          callControlId,
          tabSessionId,
          atMs: delayMs,
          iceConnected: true,
          rtpFlowing: true
        });
        if (delayMs >= Math.max(...sampleMs)) {
          stopAgentIceMediaWatchdog(session);
        }
        return;
      }

      const gatheringComplete = (stats?.iceGatheringState
        || session.connection?.iceGatheringState) === 'complete';
      // relay-only + gather done + zero relay candidates = TURN unreachable (FF 701).
      if (
        gatheringComplete
        && iceStuck
        && !hasRtpBytes
        && pcConfig?.iceTransportPolicy === 'relay'
        && !sessionLocalSdpHasRelayCandidate(session)
      ) {
        failMedia(stats, 'turn_unreachable');
        return;
      }

      // ICE connected but first RTP not yet — keep waiting until RTP grace.
      if (!iceStuck && !hasRtpBytes && delayMs < rtpGraceDeadlineMs) {
        logWebRtcEvent('sdk.iceConnectedAwaitingRtp', {
          id: session.id,
          callControlId,
          tabSessionId,
          atMs: delayMs,
          rtpGraceDeadlineMs
        });
        return;
      }

      if (delayMs < failDeadlineMs) return;

      // Still allocating TURN relay — wait for later samples (avoid Chrome hangup mid-gather).
      if (iceStuck && stillGathering && delayMs < Math.max(...sampleMs)) {
        logWebRtcEvent('sdk.iceGatheringDeferredFail', {
          id: session.id,
          callControlId,
          atMs: delayMs
        });
        return;
      }

      if (iceStuck) {
        failMedia(stats, 'ice_stuck');
        return;
      }

      if (!hasRtpBytes && delayMs >= rtpGraceDeadlineMs) {
        failMedia(stats, 'zero_rtp');
      }
    }, delayMs);
    entry.timers.push(timerId);
  });
}
